import {
  Component,
  AfterViewInit,
  ViewChild,
  ElementRef,
  HostListener,
  OnDestroy,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { DateTime } from 'luxon';
import { Subject } from 'rxjs';
import { Event } from '@app/common/models';
import { EventService } from '@app/calendar/services/event.service';

interface ScheduleEvent {
  title: string;
  start: DateTime;
  end: DateTime;
  duration: string;
  top: number;
  height: number;
  marginLeft: number;
  zIndex: number;
  uuid: string;
}

@Component({
  selector: 'pl-schedule-card',
  templateUrl: './schedule-card.component.html',
  styleUrls: ['./schedule-card.component.scss'],
})
export class ScheduleCardComponent implements AfterViewInit, OnDestroy, OnInit {
  @ViewChild('scrollContainer', { static: false }) scrollContainer: ElementRef;

  @ViewChild('scheduleCard', { static: false }) scheduleCardRef: ElementRef;

  private destroyed$ = new Subject<boolean>();

  private isDestroyed = false;

  private eventsReady = false;

  hours: { label: string; position: number }[] = [];

  majorTicks: number[] = [];

  minorTicks: number[] = [];

  events: ScheduleEvent[] = [];

  loadingEvents = true;

  hasEverScheduled: boolean | null = null;

  isHovering = false;

  scheduleCardHeight = 2160;

  minutesInDay = 24 * 60;

  totalWidth = 0;

  constructor(
    private eventService: EventService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.generateHours();
    this.generateTicks();
    this.initializeEvents();
  }

  private async initializeEvents(): Promise<void> {
    await this.checkHasEvents();
    if (this.hasEverScheduled !== false) {
      this.loadTodaysEvents();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => {
      if (this.isDestroyed) return;
      this.updateWidth();
      if (this.eventsReady) {
        this.scrollToCurrentTime();
      }
    }, 0);
  }

  ngOnDestroy(): void {
    this.isDestroyed = true;
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  @HostListener('window:resize')
  onResize(): void {
    if (this.scheduleCardRef) {
      this.updateWidth();
    }
  }

  private updateWidth(): void {
    if (this.scheduleCardRef) {
      this.totalWidth = this.scheduleCardRef.nativeElement.offsetWidth - 75;
      this.cdr.detectChanges();
    }
  }

  getEventDuration(event: ScheduleEvent): number {
    return event.end.diff(event.start, 'minutes').minutes;
  }

  private async checkHasEvents(): Promise<void> {
    try {
      this.hasEverScheduled = await this.eventService.hasEvents();
      if (this.isDestroyed) return;
      if (this.hasEverScheduled === false) {
        this.loadingEvents = false;
        this.events = [];
        this.processEvents(this.generateDemoEvents());
        this.eventsReady = true;
        this.scrollToCurrentTime();
        this.cdr.detectChanges();
      }
    } catch {
      if (this.isDestroyed) return;
      this.hasEverScheduled = null;
    }
    this.cdr.detectChanges();
  }

  private generateDemoEvents(): Event[] {
    const today = DateTime.now().startOf('day');
    const clients = [
      'Sean Maguire',
      'Jennifer Melfi',
      'Bob Newhart',
      'Linda Martin',
      'Lucy van Pelt',
    ];
    const types = [
      'Speech Therapy',
      'Occupational Therapy',
      'Behavioral Health',
      'Counseling',
      'Assessment',
    ];

    // Static schedule between 09:00 and 16:00 at 15-minute slot boundaries
    const slots: { startMinutes: number; duration: number }[] = [
      { startMinutes: 9 * 60, duration: 45 }, // 9:00 - 9:45
      { startMinutes: 10 * 60, duration: 30 }, // 10:00 - 10:30
      { startMinutes: 11 * 60, duration: 60 }, // 11:00 - 12:00
      { startMinutes: 13 * 60, duration: 15 }, // 1:00 - 1:15
      { startMinutes: 14 * 60 + 30, duration: 45 }, // 2:30 - 3:15
    ];

    return clients.map((name, i) => {
      const slot = slots[i];
      const start = today.plus({ minutes: slot.startMinutes });
      const end = start.plus({ minutes: slot.duration });
      return {
        id: `demo-${i}`,
        uuid: `demo-${i}`,
        title: `${types[i]} - ${name}`,
        start_date_local: start,
        end_date_local: end,
        clients: [],
      };
    });
  }

  private generateHours(): void {
    const minutesPerPixel = this.minutesInDay / this.scheduleCardHeight;
    for (let hour = 0; hour < 24; hour += 1) {
      const position = (hour * 60) / minutesPerPixel;
      const label = `${hour.toString().padStart(2, '0')}:00`;
      this.hours.push({ label, position });
    }
  }

  private generateTicks(): void {
    const minutesPerPixel = this.minutesInDay / this.scheduleCardHeight;
    for (let hour = 0; hour < 24; hour += 1) {
      this.majorTicks.push((hour * 60 + 30) / minutesPerPixel);
      this.minorTicks.push(
        (hour * 60 + 15) / minutesPerPixel,
        (hour * 60 + 45) / minutesPerPixel
      );
    }
  }

  private async loadTodaysEvents(): Promise<void> {
    const now = DateTime.now();
    const startOfDay = now.startOf('day');
    const endOfDay = now.endOf('day');

    try {
      const apiEvents = await this.eventService.getEvents({
        start: startOfDay,
        end: endOfDay,
      });
      if (this.isDestroyed) return;
      this.loadingEvents = false;
      this.processEvents(apiEvents);
      this.eventsReady = true;
      this.scrollToCurrentTime();
      this.cdr.detectChanges();
    } catch {
      if (this.isDestroyed) return;
      this.loadingEvents = false;
      this.events = [];
      this.cdr.detectChanges();
    }
  }

  private processEvents(apiEvents: Event[]): void {
    const localZone = DateTime.now().zoneName;
    const mapped = apiEvents
      .map((ev) => {
        // Convert from UTC to local timezone for correct positioning
        const start = ev.start_date_local.setZone(localZone);
        const end = ev.end_date_local.setZone(localZone);
        const startMinutes = start.hour * 60 + start.minute;
        const endMinutes = end.hour * 60 + end.minute;
        const minutesSpan =
          endMinutes > startMinutes
            ? endMinutes - startMinutes
            : endMinutes - startMinutes + this.minutesInDay;

        return {
          title: ev.title || 'Untitled Event',
          start,
          end,
          duration: `${start.toFormat('h:mm')} - ${end.toFormat('h:mma')}`,
          top: (startMinutes / this.minutesInDay) * this.scheduleCardHeight,
          height: (minutesSpan / this.minutesInDay) * this.scheduleCardHeight,
          marginLeft: 0,
          zIndex: 0,
          uuid: ev.uuid || ev.id,
        };
      })
      .sort((a, b) => a.start.toMillis() - b.start.toMillis());

    // Handle overlapping events
    this.events = mapped.map((event) => {
      const overlapping = mapped.filter(
        (other) =>
          other.uuid !== event.uuid &&
          event.start < other.end &&
          event.end > other.start
      );
      const eventIndex = overlapping.filter(
        (o) =>
          o.start < event.start ||
          (o.start.equals(event.start) && o.uuid < event.uuid)
      ).length;
      const marginLeft = eventIndex * 20;
      return {
        ...event,
        marginLeft,
        zIndex: eventIndex,
      };
    });
  }

  private scrollToCurrentTime(): void {
    if (!this.scrollContainer) return;
    const now = DateTime.now();
    const currentMinutes = now.hour * 60 + now.minute;
    const scrollPosition =
      (currentMinutes / this.minutesInDay) * this.scheduleCardHeight;
    this.scrollContainer.nativeElement.scrollTop = Math.max(
      0,
      scrollPosition - 90
    );
  }
}
